import React, { useCallback, useMemo, useState } from "react";
import { FiltersRequest } from "@/services";
import {
  optionsBodyTypes,
  optionsDrinking,
  optionsEyeColors,
  optionsHairColors,
  optionsMaritalStatus,
  optionsSkinTones,
  optionsSmoking,
} from "@/utils";
import { IconChevron, IconFilter } from "@/svgs";
import "./Filters.css";

interface FiltersProps {
  onFiltersChange: (filters: FiltersRequest) => void;
}

const INITIAL_STATE: FiltersRequest = {
  keyword: "",
  city: "",
  distance: 100,
  orderBy: "recent",
  minAge: undefined,
  maxAge: undefined,
  minHeight: undefined,
  maxHeight: undefined,
  maritalStatus: "",
  children: "",
  bodyType: "",
  skinTone: "",
  hairColor: "",
  eyeColor: "",
  smoking: "",
  drinking: "",
};

const Filters: React.FC<FiltersProps> = ({ onFiltersChange }) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [formData, setFormData] = useState<FiltersRequest>(INITIAL_STATE);

  const ageOptions = Array.from({ length: 48 }, (_, i) => `${18 + i}`);
  const heightOptions = Array.from({ length: 19 }, (_, i) => `${150 + i * 5}`);

  const handleChange = useCallback(
    (name: keyof FiltersRequest, value: any) => {
      const newState = {
        ...formData,
        [name]: value,
      };

      setFormData(newState);

      const cleanFilters = Object.entries(newState).reduce(
        (acc, [key, val]) => {
          if (val === "" || val === undefined) return acc;

          const numericFields = [
            "minAge",
            "maxAge",
            "minHeight",
            "maxHeight",
            "distance",
          ];

          const formattedVal = numericFields.includes(key) ? Number(val) : val;

          return {
            ...acc,
            [key]: formattedVal,
          };
        },
        {} as FiltersRequest,
      );

      onFiltersChange(cleanFilters);
    },
    [formData, onFiltersChange],
  );

  const clearFilters = () => {
    setFormData(INITIAL_STATE);
    onFiltersChange(INITIAL_STATE);
  };

  const hasFilters = JSON.stringify(formData) !== JSON.stringify(INITIAL_STATE);

  return (
    <>
      <button
        className="mobile-filter-trigger"
        onClick={() => setIsFiltersOpen(!isFiltersOpen)}
      >
        <IconFilter />
        <span>{isFiltersOpen ? "Ocultar Filtros" : "Mostrar Filtros"}</span>
        <IconChevron direction={isFiltersOpen ? "up" : "down"} />
      </button>

      <aside className={`filters ${isFiltersOpen ? "open" : "closed"}`}>
        <div className="filters-header">
          <h2>Filtros</h2>
          {hasFilters && (
            <button className="link-btn" onClick={clearFilters}>
              Limpar filtros
            </button>
          )}
        </div>

        <div className="filters-scroll-area">
          <div className="filter-group">
            <label>Nome de usuário</label>
            <input
              type="text"
              placeholder="Nome ou apelido"
              value={formData.keyword}
              onChange={(e) => handleChange("keyword", e.target.value)}
            />
          </div>

          <div className="filter-category">
            <h3>Localização</h3>

            <div className="filter-group">
              <label>Cidade</label>
              <input
                type="text"
                placeholder="Ex: São Paulo"
                value={formData.city}
                onChange={(e) => handleChange("city", e.target.value)}
              />
            </div>
          </div>

          {/* <div className="filter-category">
            <h3>Localização</h3>
            <div className="filter-group">
              <div className="slider-row">
                <label>Distância</label>
                <span className="muted">Até {formData.distance} km</span>
              </div>
              <input
                type="range"
                min={1}
                max={100}
                step={1}
                value={formData.distance}
                onChange={(e) =>
                  handleChange("distance", Number(e.target.value))
                }
              />
              <div className="slider-scale">
                <span>1 km</span>
                <span>100 km</span>
              </div>
            </div>
          </div> */}

          <div className="filter-category">
            <h3>Ordenação</h3>
            <div className="filter-group">
              <label>Ordenar por</label>
              <select
                value={formData.orderBy}
                onChange={(e) => handleChange("orderBy", e.target.value)}
              >
                <option value="recent">Mais novo</option>
                <option value="old">Mais antigo</option>
              </select>
            </div>
          </div>

          <div className="filter-category">
            <h3>Perfil</h3>
            <div className="filter-group">
              <label>Idade mínima</label>
              <select
                value={formData.minAge ?? ""}
                onChange={(e) => handleChange("minAge", e.target.value)}
              >
                <option value="">Qualquer</option>
                {ageOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Idade máxima</label>
              <select
                value={formData.maxAge ?? ""}
                onChange={(e) => handleChange("maxAge", e.target.value)}
              >
                <option value="">Qualquer</option>
                {ageOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Altura mín.</label>
              <select
                value={formData.minHeight ?? ""}
                onChange={(e) => handleChange("minHeight", e.target.value)}
              >
                <option value="">Qualquer</option>
                {heightOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt} cm
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Estado civil</label>
              <select
                value={formData.maritalStatus}
                onChange={(e) => handleChange("maritalStatus", e.target.value)}
              >
                <option value="">Todos</option>
                {optionsMaritalStatus.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="filter-category">
            <h3>Características</h3>
            {[
              {
                label: "Tipo de corpo",
                key: "bodyType",
                options: optionsBodyTypes,
              },
              {
                label: "Tom de pele",
                key: "skinTone",
                options: optionsSkinTones,
              },
              { label: "Cabelo", key: "hairColor", options: optionsHairColors },
              { label: "Olhos", key: "eyeColor", options: optionsEyeColors },
              { label: "Fuma?", key: "smoking", options: optionsSmoking },
              { label: "Bebe?", key: "drinking", options: optionsDrinking },
            ].map((f) => (
              <div className="filter-group" key={f.key}>
                <label>{f.label}</label>
                <select
                  value={formData[f.key as keyof FiltersRequest]}
                  onChange={(e) =>
                    handleChange(f.key as keyof FiltersRequest, e.target.value)
                  }
                >
                  <option value="">Todos</option>
                  {f.options.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Filters;
